/* eslint-disable */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import NotificationContainer from "../Components/Shared/NotificationContainer";
import pushNotificationService from "../services/pushNotificationService";
import notificationApiService from "../services/notificationApiService";
import config from "../config";
import { apiFetch, getStoredToken, getStoredUserRole, getNotificationStorageKey, getUnreadCountStorageKey, getKnownItemsStorageKey } from "../utils/api";
import { deleteFcmToken, onForegroundFcmMessage, requestFcmToken } from "../firebase";

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within a NotificationProvider");
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [backendNotifications, setBackendNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const seenNotificationKeysRef = useRef(new Set());
  const initialFetchDoneRef = useRef(false);

  const [pushEnabled, setPushEnabled] = useState(false);
  const [backgroundPushEnabled, setBackgroundPushEnabled] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('default');
  const previousClientIdsRef = useRef(new Set());
  const previousBookingIdsRef = useRef(new Set());
  const previousSubscriptionIdsRef = useRef(new Set());
  const previousTicketIdsRef = useRef(new Set());
  const previousReminderIdsRef = useRef(new Set());
  const previousLeadIdsRef = useRef(new Set());
  const fcmSwRegistrationRef = useRef(null);
  const isServiceWorkerContextReady = useCallback(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return false;
    if (!window.isSecureContext) return false;
    return !document.prerendering;
  }, []);

  const syncServiceWorkerAuthContext = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !isServiceWorkerContextReady()) return;

    try {
      const token = getStoredToken();
      const role = getStoredUserRole();
      const payload = {
        token: token || null,
        role: role || null,
        apiBaseUrl: config.API_BASE_URL,
        updatedAt: Date.now(),
      };

      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'SET_AUTH_CONTEXT',
          payload,
        });
      }

      const registration = await navigator.serviceWorker.ready;
      if (registration?.active) {
        registration.active.postMessage({
          type: 'SET_AUTH_CONTEXT',
          payload,
        });
      }
    } catch (err) {
      console.error('Failed to sync auth context to service worker:', err);
    }
  }, [isServiceWorkerContextReady]);

  const getFcmServiceWorkerRegistration = useCallback(async () => {
    if (!('serviceWorker' in navigator)) return null;
    if (fcmSwRegistrationRef.current) return fcmSwRegistrationRef.current;

    if (!isServiceWorkerContextReady()) {
      const becameReady = await pushNotificationService.waitForServiceWorkerContextReady();
      if (!becameReady) {
        return null;
      }
    }

    for (let attempt = 1; attempt <= 2; attempt += 1) {
      try {
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
          scope: '/firebase-cloud-messaging-push-scope',
        });
        fcmSwRegistrationRef.current = registration;
        return registration;
      } catch (error) {
        if (error?.name === 'InvalidStateError' && attempt === 1) {
          await pushNotificationService.waitForServiceWorkerContextReady();
          continue;
        }
        console.warn('Failed to register Firebase messaging service worker:', error);
        return null;
      }
    }
  }, [isServiceWorkerContextReady]);

  const registerFcmTokenWithBackend = useCallback(async (token) => {
    if (!token) return false;

    const candidateEndpoints = [
      config.API_ENDPOINTS?.FCM?.REGISTER,
      `${config.API_BASE_URL}/api/fcm/token`,
      `${config.API_BASE_URL}/api/fcm/register`,
    ].filter(Boolean);

    for (const endpoint of candidateEndpoints) {
      try {
        const response = await apiFetch(endpoint, {
          method: 'POST',
          body: JSON.stringify({
            token,
            platform: 'web',
            userAgent: navigator.userAgent,
          }),
        });

        if (response.ok) {
          return true;
        }
      } catch (error) {
        console.warn(`Failed FCM token registration at ${endpoint}:`, error);
      }
    }

    return false;
  }, []);

  const unregisterFcmTokenFromBackend = useCallback(async (token) => {
    if (!token) return false;

    const candidateEndpoints = [
      config.API_ENDPOINTS?.FCM?.UNREGISTER,
      `${config.API_BASE_URL}/api/fcm/unregister`,
      `${config.API_BASE_URL}/api/fcm/token/remove`,
    ].filter(Boolean);

    for (const endpoint of candidateEndpoints) {
      try {
        const response = await apiFetch(endpoint, {
          method: 'POST',
          body: JSON.stringify({ token }),
        });

        if (response.ok) {
          return true;
        }
      } catch (error) {
        console.warn(`Failed FCM token removal at ${endpoint}:`, error);
      }
    }

    return false;
  }, []);

  const enableFcmNotifications = useCallback(async () => {
    try {
      const registration = await getFcmServiceWorkerRegistration();
      if (!registration) return false;

      const token = await requestFcmToken(registration);
      if (!token) return false;

      return await registerFcmTokenWithBackend(token);
    } catch (error) {
      console.warn('Failed to enable Firebase notifications:', error);
      return false;
    }
  }, [getFcmServiceWorkerRegistration, registerFcmTokenWithBackend]);

  const disableFcmNotifications = useCallback(async () => {
    try {
      const registration = await getFcmServiceWorkerRegistration();
      let token = null;

      try {
        if (registration) {
          token = await requestFcmToken(registration);
        }
      } catch (error) {
        // No active FCM token to unregister from backend.
      }

      if (token) {
        await unregisterFcmTokenFromBackend(token);
      }

      await deleteFcmToken();
      return true;
    } catch (error) {
      console.warn('Failed to disable Firebase notifications:', error);
      return false;
    }
  }, [getFcmServiceWorkerRegistration, unregisterFcmTokenFromBackend]);

  // Helper to persist notification to localStorage
  const persistNotification = useCallback((notification) => {
    try {
      const storageKey = getNotificationStorageKey();
      const unreadKey = getUnreadCountStorageKey();

      const stored = localStorage.getItem(storageKey);
      const allNotifications = stored ? JSON.parse(stored) : [];

      const notificationToAdd = {
        ...notification,
        read: false,
        timestamp: new Date().toISOString(),
        // Ensure we have both timestamp and time for compatibility
        time: notification.time || new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        }),
        // Include source reference for redirection
        sourceType: notification.sourceType || null,
        sourceId: notification.sourceId || null,
      };

      // Add to start of array
      const updated = [notificationToAdd, ...allNotifications].slice(0, 100);
      localStorage.setItem(storageKey, JSON.stringify(updated));

      // Update unread count
      const unread = updated.filter(n => !n.read).length;
      localStorage.setItem(unreadKey, unread.toString());

      // Notify other components
      window.dispatchEvent(new Event('notificationUpdated'));

      console.log('NotificationContext: Persisted new notification:', notification.title);
    } catch (error) {
      console.error('Error persisting notification:', error);
    }
  }, []);

  const showNotification = useCallback((notification) => {
    const id = notification.id || (Date.now() + Math.random());
    const newNotification = {
      id,
      type: notification.type || "info", // Default to info
      title: notification.title || "New Notification",
      message: notification.message || "",
      time: notification.time || new Date().toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }),
      // Include source reference for redirection
      sourceType: notification.sourceType || null,
      sourceId: notification.sourceId || null,
      deepLink: notification.deepLink || null,
      notificationKey: notification.notificationKey || null,
    };

    setNotifications((prev) => [...prev, newNotification]);

    // Only persist to localStorage if not explicitly disabled (e.g. backend DB notifications)
    if (notification.persist !== false) {
      persistNotification(newNotification);
    }

    return id;
  }, [persistNotification]);

  // Semantic Helpers
  const success = useCallback((title, message) => {
    return showNotification({ type: 'success', title, message });
  }, [showNotification]);

  const error = useCallback((title, message) => {
    return showNotification({ type: 'error', title, message });
  }, [showNotification]);

  const info = useCallback((title, message) => {
    return showNotification({ type: 'info', title, message });
  }, [showNotification]);

  const warn = useCallback((title, message) => {
    return showNotification({ type: 'warning', title, message });
  }, [showNotification]);

  const warning = warn;

  // Authoritative Backend Notification API Methods
  const refreshUnreadCount = useCallback(async () => {
    const token = getStoredToken();
    if (!token) return;
    try {
      const res = await notificationApiService.fetchUnreadCount();
      if (res?.success && res.data) {
        const count = res.data.unreadCount ?? 0;
        setUnreadCount(count);
        window.dispatchEvent(new CustomEvent('notificationUpdated', { detail: { unreadCount: count } }));
      }
    } catch (err) {
      console.warn('Failed to refresh unread count from backend:', err?.message || err);
    }
  }, []);

  const fetchBackendNotifications = useCallback(async ({ page = 1, limit = 20, unreadOnly = false, isBackground = false } = {}) => {
    const token = getStoredToken();
    if (!token) return;
    if (!isBackground) setLoading(true);
    try {
      const res = await notificationApiService.fetchNotifications({ page, limit, unreadOnly });
      if (res?.success && Array.isArray(res.data)) {
        const items = res.data;
        setBackendNotifications(items);
        if (res.pagination) {
          setPagination(res.pagination);
          if (res.pagination.unreadCount !== undefined) {
            setUnreadCount(res.pagination.unreadCount);
          }
        }

        if (!initialFetchDoneRef.current) {
          // Initial fetch: mark all existing keys as seen without toasting historical items
          items.forEach((item) => {
            const key = item.notificationKey || `notif_${item.id}`;
            seenNotificationKeysRef.current.add(key);
          });
          initialFetchDoneRef.current = true;
        } else {
          // Subsequent fetch: toast only genuinely new notifications whose channels include 'toast'
          items.forEach((item) => {
            const key = item.notificationKey || `notif_${item.id}`;
            if (!seenNotificationKeysRef.current.has(key)) {
              seenNotificationKeysRef.current.add(key);
              const channels = item.metadata?.channels || [];
              if (channels.includes('toast') || item.channels?.includes('toast')) {
                showNotification({
                  id: item.id,
                  title: item.title || 'Notification',
                  message: item.message || '',
                  type: item.priority === 'HIGH' ? 'warning' : 'info',
                  deepLink: item.deepLink,
                  notificationKey: item.notificationKey,
                  persist: false, // Do not duplicate into localStorage
                });
              }
            }
          });
        }
      }
    } catch (err) {
      console.warn('Failed to fetch backend notifications:', err?.message || err);
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [showNotification]);

  const markNotificationAsRead = useCallback(async (id) => {
    try {
      // Optimistic update
      setBackendNotifications((prev) =>
        prev.map((n) => (String(n.id) === String(id) ? { ...n, isRead: true, readAt: new Date().toISOString() } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));

      const res = await notificationApiService.markAsRead(id);
      refreshUnreadCount();
      return res;
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      fetchBackendNotifications({ isBackground: true });
      refreshUnreadCount();
      throw err;
    }
  }, [fetchBackendNotifications, refreshUnreadCount]);

  const markAllNotificationsAsRead = useCallback(async () => {
    try {
      // Optimistic update
      setBackendNotifications((prev) =>
        prev.map((n) => ({ ...n, isRead: true, readAt: new Date().toISOString() }))
      );
      setUnreadCount(0);

      const res = await notificationApiService.markAllAsRead();
      refreshUnreadCount();
      return res;
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
      fetchBackendNotifications({ isBackground: true });
      refreshUnreadCount();
      throw err;
    }
  }, [fetchBackendNotifications, refreshUnreadCount]);

  useEffect(() => {
    let unsubscribe = () => {};

    onForegroundFcmMessage((payload) => {
      const title = payload?.notification?.title || payload?.data?.title || "New Notification";
      const message =
        payload?.notification?.body ||
        payload?.data?.body ||
        payload?.data?.message ||
        "";

      showNotification({
        type: "info",
        title,
        message,
        sourceType: payload?.data?.type || null,
        sourceId: payload?.data?.id || null,
      });
    })
      .then((listenerUnsubscribe) => {
        if (typeof listenerUnsubscribe === "function") {
          unsubscribe = listenerUnsubscribe;
        }
      })
      .catch((error) => {
        console.warn("FCM foreground listener setup failed:", error);
      });

    return () => {
      unsubscribe();
    };
  }, [showNotification]);

  /**
   * Helper to detect and notify for missed items since last session
   * @param {string} category - 'leads', 'tickets', etc.
   * @param {Array} items - List of fresh items from API
   * @param {Function} notificationFn - Function to call for each new item
   * @param {React.MutableRefObject} ref - Ref holding current session's known IDs
   */
  const detectAndNotify = useCallback((category, items, notificationFn, ref) => {
    try {
      const storageKey = getKnownItemsStorageKey(category);
      const stored = localStorage.getItem(storageKey);
      const isBaseline = stored === null;

      // Load known IDs from storage
      const knownIds = new Set(stored ? JSON.parse(stored) : []);

      // Initialize Ref from storage on first run if needed
      if (ref.current.size === 0 && knownIds.size > 0) {
        ref.current = knownIds;
      }

      const currentIds = items.map(i => String(i.id));

      // Items are "new/missed" if they aren't in our persistent known set
      const missedItems = items.filter(item => !knownIds.has(String(item.id)));

      if (isBaseline) {
        // Establish baseline for a new user/device without spamming
        localStorage.setItem(storageKey, JSON.stringify(currentIds));
        ref.current = new Set(currentIds);
        console.log(`NotificationContext: Baseline established for ${category}`);
        return;
      }

      if (missedItems.length > 0) {
        console.log(`NotificationContext: Detected ${missedItems.length} missed ${category}`);

        if (missedItems.length > 5) {
          // Provide summary for high volume of missed items
          const typeLabel = category.charAt(0).toUpperCase() + category.slice(1);
          info(`Missed ${typeLabel}`, `You have ${missedItems.length} new ${category} since your last session.`);
          // Alert individually for the 3 most recent for context
          missedItems.slice(0, 3).forEach(item => notificationFn(item));
        } else {
          // Alert individually for small batches
          missedItems.forEach(item => notificationFn(item));
        }

        // Update persistent and in-memory state
        const updatedIdsSet = new Set([...Array.from(knownIds), ...currentIds]);
        // Keep history manageable (last 500 IDs)
        const updatedIdsArray = Array.from(updatedIdsSet).slice(-500);

        localStorage.setItem(storageKey, JSON.stringify(updatedIdsArray));
        ref.current = new Set(updatedIdsArray);
      }
    } catch (err) {
      console.error(`Error checking missed ${category}:`, err);
    }
  }, [info]);

  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
  }, []);

  const showContactNotification = useCallback(async (clientName, clientId = null) => {
    const role = getStoredUserRole();
    const isAdmin = role === 'admin' || role === 'superadmin';
    if (!isAdmin) return null;

    const notification = {
      type: "contact",
      title: "New Client Added",
      message: `${clientName} has been added through the contact form.`,
      sourceType: "client",
      sourceId: clientId,
    };

    // Show in-app notification
    const id = showNotification(notification);

    // Always show browser notification if permission is granted (works even when browser is closed/minimized)
    if ('serviceWorker' in navigator && 'Notification' in window) {
      try {
        const permission = Notification.permission;
        if (permission === 'granted') {
          const registration = await navigator.serviceWorker.ready;
          await registration.showNotification(notification.title, {
            body: notification.message,
            icon: '/logo192.png',
            badge: '/logo192.png',
            tag: `new-client-${Date.now()}`, // Unique tag to show multiple notifications
            data: { type: 'contact', clientName, url: '/' },
            vibrate: [200, 100, 200],
            requireInteraction: false,
            silent: false
          });
        }
      } catch (error) {
        console.error('Error showing browser notification:', error);
      }
    }

    return id;
  }, [showNotification]);

  const showBookingNotification = useCallback(async (bookingName, bookingId = null) => {
    const role = getStoredUserRole();
    const isAdmin = role === 'admin' || role === 'superadmin';
    if (!isAdmin) return null;

    const notification = {
      type: "booking",
      title: "New Booking Received",
      message: `${bookingName} has submitted a booking request.`,
      sourceType: "booking",
      sourceId: bookingId,
    };

    // Show in-app notification
    const id = showNotification(notification);

    // Always show browser notification if permission is granted (works even when browser is closed/minimized)
    if ('serviceWorker' in navigator && 'Notification' in window) {
      try {
        const permission = Notification.permission;
        if (permission === 'granted') {
          const registration = await navigator.serviceWorker.ready;
          await registration.showNotification(notification.title, {
            body: notification.message,
            icon: '/logo192.png',
            badge: '/logo192.png',
            tag: `new-booking-${Date.now()}`, // Unique tag to show multiple notifications
            data: { type: 'booking', bookingName, url: '/' },
            vibrate: [200, 100, 200],
            requireInteraction: false,
            silent: false
          });
        }
      } catch (error) {
        console.error('Error showing browser notification:', error);
      }
    }

    return id;
  }, [showNotification]);

  const showSubscriptionNotification = useCallback(async (userName, planName, subscriptionId = null) => {
    const role = getStoredUserRole();
    const isAdmin = role === 'admin' || role === 'superadmin';
    if (!isAdmin) return null;

    const notification = {
      type: "subscription",
      title: "New Subscription Activated",
      message: `${userName} has activated the ${planName} plan.`,
      sourceType: "subscription",
      sourceId: subscriptionId,
    };

    // Show in-app notification
    const id = showNotification(notification);

    // Always show browser notification if permission is granted
    if ('serviceWorker' in navigator && 'Notification' in window) {
      try {
        const permission = Notification.permission;
        if (permission === 'granted') {
          const registration = await navigator.serviceWorker.ready;
          await registration.showNotification(notification.title, {
            body: notification.message,
            icon: '/logo192.png',
            badge: '/logo192.png',
            tag: `new-subscription-${Date.now()}`,
            data: { type: 'subscription', userName, planName, url: '/' },
            vibrate: [200, 100, 200],
            requireInteraction: false,
            silent: false
          });
        }
      } catch (error) {
        console.error('Error showing browser notification:', error);
      }
    }

    return id;
  }, [showNotification]);

  const showTicketNotification = useCallback(async (customerName, ticketSubject, ticketId = null) => {
    const role = getStoredUserRole();
    const isAdmin = role === 'admin' || role === 'superadmin';
    if (!isAdmin) return null;

    const notification = {
      type: "ticket",
      title: "New Support Ticket",
      message: `${customerName} has created a ticket: ${ticketSubject}`,
      sourceType: "ticket",
      sourceId: ticketId,
    };

    // Show in-app notification
    const id = showNotification(notification);

    // Always show browser notification if permission is granted
    if ('serviceWorker' in navigator && 'Notification' in window) {
      try {
        const permission = Notification.permission;
        if (permission === 'granted') {
          const registration = await navigator.serviceWorker.ready;
          await registration.showNotification(notification.title, {
            body: notification.message,
            icon: '/logo192.png',
            badge: '/logo192.png',
            tag: `new-ticket-${Date.now()}`,
            data: { type: 'ticket', customerName, ticketSubject, url: '/' },
            vibrate: [200, 100, 200],
            requireInteraction: false,
            silent: false
          });
        }
      } catch (error) {
        console.error('Error showing browser notification:', error);
      }
    }

    return id;
  }, [showNotification]);

  const showReminderNotification = useCallback(async (leadName, followUpTime, feedback, leadId = null) => {
    // Generate specialized title and message based on feedback type
    let title = "Follow-up Reminder";
    let message = `You have a follow-up scheduled for ${leadName} at ${followUpTime || 'today'}.`;

    if (feedback === "Call back") {
      title = "Call Back Requested";
      message = `You have a call back scheduled with ${leadName} at ${followUpTime || 'today'}.`;
    } else if (feedback === "Site visit proposed") {
      title = "Site Visit Requested";
      message = `Site visit scheduled for ${leadName} today at ${followUpTime || 'today'}.`;
    }

    const notification = {
      type: "reminder",
      title: title,
      message: message,
      sourceType: "lead", // Reminders are linked to leads
      sourceId: leadId,
    };

    // Show in-app notification
    const id = showNotification(notification);

    // Always show browser notification if permission is granted
    if ('serviceWorker' in navigator && 'Notification' in window) {
      try {
        const permission = Notification.permission;
        if (permission === 'granted') {
          const registration = await navigator.serviceWorker.ready;
          await registration.showNotification(notification.title, {
            body: notification.message,
            icon: '/logo192.png',
            badge: '/logo192.png',
            tag: `reminder-${Date.now()}`,
            data: { type: 'reminder', leadName, followUpTime, feedback, url: '/leads' },
            vibrate: [200, 100, 200],
            requireInteraction: false,
            silent: false
          });
        }
      } catch (error) {
        console.error('Error showing browser notification:', error);
      }
    }

    return id;
  }, [showNotification]);

  const showLeadAssignmentNotification = useCallback(async (leadName, assignedBy = null, leadId = null) => {
    const role = getStoredUserRole();
    const isUser = role !== 'admin' && role !== 'superadmin';
    if (!isUser) return null;

    const notification = {
      type: "lead",
      title: "New Lead Assigned",
      message: assignedBy
        ? `A new lead "${leadName}" has been assigned to you by ${assignedBy}.`
        : `A new lead "${leadName}" has been assigned to you.`,
      sourceType: "lead",
      sourceId: leadId,
    };

    // Show in-app notification
    const id = showNotification(notification);

    // Always show browser notification if permission is granted
    if ('serviceWorker' in navigator && 'Notification' in window) {
      try {
        const permission = Notification.permission;
        if (permission === 'granted') {
          const registration = await navigator.serviceWorker.ready;
          await registration.showNotification(notification.title, {
            body: notification.message,
            icon: '/logo192.png',
            badge: '/logo192.png',
            tag: `new-lead-${Date.now()}`,
            data: { type: 'lead', leadName, assignedBy, url: '/leads' },
            vibrate: [200, 100, 200],
            requireInteraction: false,
            silent: false
          });
        }
      } catch (error) {
        console.error('Error showing browser notification:', error);
      }
    }

    return id;
  }, [showNotification]);

  // Global polling service - runs regardless of which page is active
  useEffect(() => {
    // Fetch and check for new clients
    const fetchClients = async () => {
      try {
        const role = getStoredUserRole();
        const isAdmin = role === 'admin' || role === 'superadmin';
        if (!isAdmin) return;

        const response = await apiFetch(config.API_ENDPOINTS.CONTACT);

        if (!response.ok) {
          return;
        }

        const responseData = await response.json();
        const contacts = responseData.data || [];

        const mappedClients = contacts.map((contact) => ({
          id: contact.id,
          fullName: contact.fullName || "",
        }));

        // Use centralized missed notification helper
        detectAndNotify(
          'clients',
          mappedClients,
          (client) => showContactNotification(client.fullName, client.id),
          previousClientIdsRef
        );
      } catch (err) {
        console.error("Error in global client polling:", err);
      }
    };

    // Fetch and check for new bookings
    const fetchBookings = async () => {
      try {
        const role = getStoredUserRole();
        const isAdmin = role === 'admin' || role === 'superadmin';
        if (!isAdmin) return;

        const response = await apiFetch(config.API_ENDPOINTS.BOOKINGS);

        if (!response.ok) {
          return;
        }

        const responseData = await response.json();
        const bookingsData = responseData.data || [];

        const mappedBookings = bookingsData.map((booking) => ({
          id: booking.id,
          name: booking.name || "",
        }));

        // Use centralized missed notification helper
        detectAndNotify(
          'bookings',
          mappedBookings,
          (booking) => showBookingNotification(booking.name, booking.id),
          previousBookingIdsRef
        );
      } catch (err) {
        console.error("Error in global booking polling:", err);
      }
    };

    const fetchSubscriptions = async () => {
      try {
        const token = getStoredToken();
        const role = getStoredUserRole();
        const isAdmin = role === 'admin' || role === 'superadmin';
        if (!token || !isAdmin) return;

        // Verify admin role if possible, or just let API return error
        const response = await apiFetch(`${config.API_BASE_URL}/api/subscriptions/stats`);
        if (!response.ok) return;

        const responseData = await response.json();
        // For now, we'll poll for all subscriptions to detect new ones
        // In a real implementation, you'd have an endpoint that returns recent subscriptions
        // For now, we'll trigger notifications from the frontend when subscriptions are activated
      } catch (err) {
        console.error("Error in subscription polling:", err);
      }
    };

    const fetchTickets = async () => {
      try {
        const token = getStoredToken();
        const role = getStoredUserRole();
        const isAdmin = role === 'admin' || role === 'superadmin';
        if (!token || !isAdmin) return;

        const response = await apiFetch(`${config.API_BASE_URL}/api/tickets`);
        if (!response.ok) return;

        const responseData = await response.json();
        const tickets = responseData.data?.tickets || [];

        // Use centralized missed notification helper
        detectAndNotify(
          'tickets',
          tickets,
          (ticket) => showTicketNotification(ticket.customerName, ticket.subject, ticket.id),
          previousTicketIdsRef
        );
      } catch (err) {
        console.error("Error in global ticket polling:", err);
      }
    };

    const fetchReminders = async () => {
      try {
        const token = getStoredToken();
        if (!token) return;

        const response = await apiFetch(config.API_ENDPOINTS.LEADS_REMINDERS);
        if (!response.ok) return;

        const responseData = await response.json();
        const reminders = responseData.data || [];

        // Use centralized missed notification helper
        detectAndNotify(
          'reminders',
          reminders,
          (reminder) => showReminderNotification(reminder.name, reminder.followUpTime, reminder.feedback, reminder.leadId || reminder.id),
          previousReminderIdsRef
        );
      } catch (err) {
        console.error("Error in reminder polling:", err);
      }
    };

    const fetchAssignedLeads = async () => {
      try {
        const token = getStoredToken();
        const role = getStoredUserRole();
        const isAdmin = role === 'admin' || role === 'superadmin';

        if (!token || isAdmin) return;

        // For non-admin users, we still might need parentId for proper filtering
        // Fetch current user once if not available
        const meResponse = await apiFetch(config.API_ENDPOINTS.AUTH.ME);
        if (!meResponse.ok) return;

        const meData = await meResponse.json();
        const currentUser = meData?.data?.user || meData?.user || meData?.data || meData;

        const response = await apiFetch(config.API_ENDPOINTS.LEADS);
        if (!response.ok) return;

        const responseData = await response.json();
        // Handle different response structures
        const leads = Array.isArray(responseData.data)
          ? responseData.data
          : Array.isArray(responseData)
            ? responseData
            : [];

        // Get account owner ID (if subuser, use parent; otherwise use own ID)
        const accountOwnerId = currentUser?.parentId || currentUser?.id;
        const currentUserId = currentUser?.id;

        // Get all account user IDs (parent + subusers) for proper filtering
        // The backend returns leads where ownerId matches any account user
        const allAccountUserIds = [accountOwnerId];
        if (currentUserId && currentUserId !== accountOwnerId) {
          allAccountUserIds.push(currentUserId);
        }

        // Filter leads assigned to this user's account
        // Check both ownerId and userId fields to catch all assigned leads
        const assignedLeads = leads.filter(lead => {
          const leadOwnerId = lead.ownerId || lead.userId;
          const leadUserId = lead.userId || lead.ownerId;

          // Lead is assigned if ownerId or userId matches any account user
          return allAccountUserIds.includes(leadOwnerId) ||
            allAccountUserIds.includes(leadUserId) ||
            leadOwnerId === accountOwnerId ||
            leadUserId === accountOwnerId;
        });

        const mappedLeads = assignedLeads.map((lead) => {
          // Identify the assigner. 
          // If assignedTo is current user, the assigner is likely the admin.
          const assigner = lead.assignedBy || (lead.assignedTo === currentUser.name ? "Sambhav Pro" : lead.assignedTo) || "Sambhav Pro";

          return {
            id: lead.id,
            name: lead.name || "New Lead",
            assignedBy: assigner,
          };
        });

        // Use centralized missed notification helper
        detectAndNotify(
          'leads',
          mappedLeads,
          (lead) => showLeadAssignmentNotification(lead.name, lead.assignedBy, lead.id),
          previousLeadIdsRef
        );
      } catch (err) {
        console.error("Error in lead assignment polling:", err);
      }
    };

    // Unified Polling logic - using a single interval for all background tasks
    const runAllPolls = () => {
      fetchClients();
      fetchBookings();
      // Phase 7: Tickets, Assigned Leads, and Reminders duplicate Phase 6A backend notifications.
      // Removed local polling for them to ensure backend DB remains the single source of truth.
      refreshUnreadCount();
      fetchBackendNotifications({ isBackground: true });
    };

    let pollingInterval;
    const startPolling = (intervalTime) => {
      if (pollingInterval) clearInterval(pollingInterval);
      pollingInterval = setInterval(runAllPolls, intervalTime);
    };

    // Initial fetch of backend notifications & unread count, then poll every 60 s
    fetchBackendNotifications();
    refreshUnreadCount();
    runAllPolls();
    startPolling(60000);

    // When page becomes visible, check immediately
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible, refresh immediately
        fetchClients();
        fetchBookings();
        refreshUnreadCount();
        fetchBackendNotifications({ isBackground: true });

        // Also trigger service worker to check (in case browser was closed)
        if ('serviceWorker' in navigator && isServiceWorkerContextReady()) {
          navigator.serviceWorker.ready.then((registration) => {
            if (registration.active) {
              registration.active.postMessage({ type: 'CHECK_NOTIFICATIONS' });
            }
          }).catch((error) => {
            console.warn('Service worker not ready on visibility change:', error);
          });
        }
      }
    };

    // When browser comes back online, check immediately
    const handleOnline = () => {
      fetchClients();
      fetchBookings();
      refreshUnreadCount();
      fetchBackendNotifications({ isBackground: true });

      // Trigger service worker to check
      if ('serviceWorker' in navigator && isServiceWorkerContextReady()) {
        navigator.serviceWorker.ready.then((registration) => {
          if (registration.active) {
            registration.active.postMessage({ type: 'CHECK_NOTIFICATIONS' });
          }
        }).catch((error) => {
          console.warn('Service worker not ready on online event:', error);
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [isServiceWorkerContextReady, showContactNotification, showBookingNotification, showTicketNotification, showReminderNotification, showLeadAssignmentNotification]);

  // Listen for subscription, ticket, and lead assignment events
  useEffect(() => {
    const handleNewSubscription = (event) => {
      const { subscription, planName } = event.detail || {};
      const role = getStoredUserRole();
      const isAdmin = role === 'admin' || role === 'superadmin';

      if (subscription && planName && isAdmin) {
        // Get user name from subscription or use plan name
        const userName = subscription.user?.name || subscription.userId || 'A user';
        showSubscriptionNotification(userName, planName, subscription.id);
      }
    };

    const handleNewTicket = (event) => {
      const { ticket } = event.detail || {};
      const role = getStoredUserRole();
      const isAdmin = role === 'admin' || role === 'superadmin';

      if (ticket && isAdmin) {
        showTicketNotification(
          ticket.customerName || 'Unknown',
          ticket.subject || 'New Ticket',
          ticket.id
        );
      }
    };

    const handleLeadAssigned = (event) => {
      const { lead, assignedBy } = event.detail || {};
      const role = getStoredUserRole();
      const isUser = role !== 'admin' && role !== 'superadmin';

      if (lead && isUser) {
        // Only show if we haven't already notified for this lead via polling recently
        if (!previousLeadIdsRef.current.has(lead.id)) {
          showLeadAssignmentNotification(lead.name, assignedBy || "Sambhav Pro", lead.id);
          // Add to seen set to prevent duplicate from polling
          previousLeadIdsRef.current.add(lead.id);
        }
      }
    };

    window.addEventListener('newSubscriptionActivated', handleNewSubscription);
    window.addEventListener('newTicketCreated', handleNewTicket);
    window.addEventListener('leadAssigned', handleLeadAssigned);

    return () => {
      window.removeEventListener('newSubscriptionActivated', handleNewSubscription);
      window.removeEventListener('newTicketCreated', handleNewTicket);
      window.removeEventListener('leadAssigned', handleLeadAssigned);
    };
  }, [showSubscriptionNotification, showTicketNotification, showLeadAssignmentNotification]);

  // Initialize service worker and push notifications
  useEffect(() => {
    const initializeNotifications = async () => {
      if (!pushNotificationService.isSupported()) {
        console.log('Browser notifications not supported');
        return;
      }
      if (!isServiceWorkerContextReady()) {
        await pushNotificationService.waitForServiceWorkerContextReady();
        if (!isServiceWorkerContextReady()) {
          console.log('Notification initialization deferred: service worker context still inactive');
          return;
        }
      }

      try {
        // Check permission status
        const permission = await pushNotificationService.getPermissionStatus();
        setPermissionStatus(permission);

        // Always register service worker (needed for browser notifications to work)
        let registration;
        try {
          registration = await pushNotificationService.registerServiceWorker();
          console.log('Service worker registered for browser notifications');
        } catch (error) {
          console.error('Service worker registration failed:', error);
          return;
        }

        // Register periodic background sync (works even when browser is closed)
        // This allows the service worker to check for new contacts/bookings periodically
        if (registration && 'periodicSync' in registration && 'serviceWorker' in navigator) {
          try {
            const permission = await navigator.permissions.query({ name: 'periodic-background-sync' });
            if (permission.state === 'granted') {
              await registration.periodicSync.register('check-new-contacts', {
                minInterval: 300000 // 5 minutes minimum
              });
              await registration.periodicSync.register('check-new-bookings', {
                minInterval: 300000 // 5 minutes minimum
              });
              await registration.periodicSync.register('check-new-tickets', {
                minInterval: 300000 // 5 minutes minimum
              });
              await registration.periodicSync.register('check-new-leads', {
                minInterval: 300000 // 5 minutes minimum
              });
              await registration.periodicSync.register('check-new-reminders', {
                minInterval: 300000 // 5 minutes minimum
              });
              console.log('Periodic background sync registered');
            } else {
              console.log('Periodic background sync permission not granted');
            }
          } catch (error) {
            console.log('Periodic background sync not available:', error);
            // Fallback: Register background sync for when browser comes online
            if ('sync' in registration) {
              try {
                await registration.sync.register('sync-notifications');
                console.log('Background sync registered as fallback');
              } catch (syncError) {
                console.log('Background sync not available:', syncError);
              }
            }
          }
        } else if ('sync' in registration) {
          // Fallback to regular background sync
          try {
            await registration.sync.register('sync-notifications');
            console.log('Background sync registered');
          } catch (syncError) {
            console.log('Background sync not available:', syncError);
          }
        }

        // If permission is granted, browser notifications are enabled
        if (permission === 'granted') {
          setPushEnabled(true);
          console.log('Browser notifications enabled (permission granted)');

          let closedAppDeliveryEnabled = false;

          try {
            const subscription = await pushNotificationService.getSubscription();
            if (subscription) {
              console.log('Backend push notifications subscription active');
              closedAppDeliveryEnabled = true;
            } else {
              await pushNotificationService.subscribe();
              console.log('Subscribed to backend push notifications');
              closedAppDeliveryEnabled = true;
            }
          } catch (error) {
            console.log('Web Push subscription not available, trying Firebase Cloud Messaging...');
          }

          if (!closedAppDeliveryEnabled) {
            const fcmEnabled = await enableFcmNotifications();
            if (fcmEnabled) {
              console.log('Firebase Cloud Messaging token registered for background notifications');
              closedAppDeliveryEnabled = true;
            }
          }

          setBackgroundPushEnabled(closedAppDeliveryEnabled);
          if (!closedAppDeliveryEnabled) {
            console.log('Closed-app notifications are disabled. Backend token registration is missing.');
          }
        } else {
          setPushEnabled(false);
          setBackgroundPushEnabled(false);
        }
      } catch (error) {
        console.error('Notification initialization failed:', error);
        setBackgroundPushEnabled(false);
      }
    };

    initializeNotifications();
  }, [enableFcmNotifications, isServiceWorkerContextReady]);

  useEffect(() => {
    syncServiceWorkerAuthContext();

    const handleStorageChange = (event) => {
      if (event.key === 'authToken') {
        syncServiceWorkerAuthContext();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [syncServiceWorkerAuthContext]);

  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    const handleServiceWorkerMessage = (event) => {
      if (!event.data || event.data.type !== 'NAVIGATE_NOTIFICATION') return;

      const payload = event.data.payload || {};
      const targetUrl = payload.deepLink || payload.url || '/';

      if (typeof targetUrl === 'string') {
        const trimmed = targetUrl.trim();
        // Validate internal application route (starts with single slash, not protocol-relative //)
        if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
          if (window.location.pathname + window.location.search !== trimmed) {
            window.location.assign(trimmed);
          }
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, []);

  // Enable push notifications
  const enablePushNotifications = useCallback(async () => {
    try {
      console.log('Requesting notification permission...');
      const permission = await pushNotificationService.requestPermission();
      setPermissionStatus(permission);
      console.log('Permission status:', permission);

      if (permission === 'granted') {
        console.log('Permission granted');

        // Browser notifications are now enabled (they work with just permission)
        // Set pushEnabled to true because browser notifications ARE enabled
        setPushEnabled(true);

        let closedAppDeliveryEnabled = false;

        try {
          await pushNotificationService.subscribe();
          console.log('Backend push notifications enabled (browser notifications also enabled)');
          closedAppDeliveryEnabled = true;
        } catch (subscribeError) {
          const errorMsg = subscribeError.message || '';
          if (errorMsg.includes('VAPID') || errorMsg.includes('applicationServerKey')) {
            console.log('Web Push backend registration unavailable, trying Firebase Cloud Messaging...');
          } else {
            console.warn('Backend push subscription failed:', subscribeError);
          }
        }

        if (!closedAppDeliveryEnabled) {
          closedAppDeliveryEnabled = await enableFcmNotifications();
        }

        setBackgroundPushEnabled(closedAppDeliveryEnabled);

        // Return true because notifications ARE enabled
        return true;
      } else if (permission === 'denied') {
        console.warn('Notification permission denied by user');
        return false;
      } else {
        console.warn('Notification permission not granted');
        return false;
      }
    } catch (error) {
      console.error('Failed to enable notifications:', error);
      // Only throw if it's a permission error, not a VAPID key error
      if (error.message && error.message.includes('permission')) {
        throw error;
      }
      // For VAPID key errors, just return false (browser notifications might still work)
      return false;
    }
  }, [enableFcmNotifications]);

  // Disable push notifications
  const disablePushNotifications = useCallback(async () => {
    try {
      const webPushDisabled = await pushNotificationService.unsubscribe().catch(() => false);
      const fcmDisabled = await disableFcmNotifications();
      // Always update state to false, even if unsubscribe had issues
      setPushEnabled(false);
      setBackgroundPushEnabled(false);
      console.log('Push notifications disabled');
      return webPushDisabled || fcmDisabled;
    } catch (error) {
      console.error('Failed to disable push notifications:', error);
      // Still set to false even if there was an error
      setPushEnabled(false);
      setBackgroundPushEnabled(false);
      return false;
    }
  }, [disableFcmNotifications]);

  return (
    <NotificationContext.Provider
      value={{
        showNotification,
        success,
        error,
        info,
        warning,
        warn,
        showContactNotification,
        showBookingNotification,
        showSubscriptionNotification,
        showTicketNotification,
        showReminderNotification,
        showLeadAssignmentNotification,
        removeNotification,
        backendNotifications,
        unreadCount,
        loading,
        pagination,
        fetchBackendNotifications,
        refreshUnreadCount,
        markNotificationAsRead,
        markAllNotificationsAsRead,
        pushEnabled,
        backgroundPushEnabled,
        permissionStatus,
        enablePushNotifications,
        disablePushNotifications,
        isPushSupported: pushNotificationService.isSupported(),
      }}
    >
      {children}
      <NotificationContainer
        notifications={notifications}
        onRemoveNotification={removeNotification}
      />
    </NotificationContext.Provider>
  );
};
